import { Button, Dialog, Input, Select, WidgetSizeEnum } from '@wonderschool/common-base-ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { functions } from '../../api/firebase/firebase';
import { usePrimaryLocation } from '../../hooks/useLocations';
import { useUser } from '../../hooks/useUser';
import { US_STATE_OPTIONS } from './usStateOptions';

/**
 * Minimal shape this modal reads off the organization. The org object is loosely
 * typed across CCMS, so we only declare the fields we prefill from and keep them
 * optional to avoid over-committing to a schema we don't own.
 */
type PayoutVerificationOrganization = {
  id: string;
  name?: string;
};

type PayoutVerificationModalProps = {
  organization?: PayoutVerificationOrganization;
  open: boolean;
  onClose: () => void;
};

/**
 * Self-serve payout-verification intake (CCMS-7942). Providers file the request
 * here from the VerifyPayoutBanner; the callable records it on the org
 * (payoutVerification.status = 'pending') and pings support in Slack. Once the org
 * refreshes with the pending status the banner flips to its "in review" state, so
 * on success we simply close the modal.
 */
export default function PayoutVerificationModal({ organization, open, onClose }: PayoutVerificationModalProps) {
  const { t } = useTranslation();
  const user = useUser();
  // The program's address (with state) lives on its primary location, not the org.
  const primaryLocation = usePrimaryLocation();

  const [licenseNumber, setLicenseNumber] = useState('');
  const [state, setState] = useState(primaryLocation?.state ?? '');
  const [programName, setProgramName] = useState(organization?.name ?? '');
  const [contactEmail, setContactEmail] = useState(user?.email ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // The modal stays mounted (open toggles the Dialog), so the primary location may
  // not be in the store yet at first render. Seed the state once it arrives, but
  // never clobber a value the provider has already selected.
  useEffect(() => {
    if (primaryLocation?.state) {
      setState((current) => current || primaryLocation.state);
    }
  }, [primaryLocation?.state]);

  // Require the fields support needs to act on the request, so a blank submission
  // can't generate an unactionable Slack ticket. License # comes from the provider
  // and isn't in our data, so it stays optional here.
  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail.trim());
  const canSubmit = Boolean(state) && isValidEmail && !submitting;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      await functions().httpsCallable('callables-stripe-submitPayoutVerification')({
        organizationId: organization?.id,
        licenseNumber,
        state,
        programName,
        contactEmail,
      });
      // The org will refresh with payoutVerification.status = 'pending', which flips
      // the banner to its "in review" state. Just close here.
      onClose();
    } catch {
      setError(t('verifyPayout.form.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog isOpen={open} onClose={onClose} hasCloseButton data-testid="payout-verification-modal">
      <Dialog.Panel>
        <Dialog.Title>{t('verifyPayout.form.title')}</Dialog.Title>
        <Dialog.Description>{t('verifyPayout.form.subtitle')}</Dialog.Description>
        <div className="mt-4 flex flex-col gap-4">
          <Input
            label={t('verifyPayout.form.licenseLabel')}
            value={licenseNumber}
            onChange={(e) => setLicenseNumber(e.target.value)}
            data-testid="payout-verification-license"
          />
          <Select
            label={t('verifyPayout.form.stateLabel')}
            placeholder={t('verifyPayout.form.statePlaceholder')}
            options={US_STATE_OPTIONS}
            value={US_STATE_OPTIONS.find((option) => option.value === state)}
            onChange={(option) => setState(option?.value ?? '')}
            searchable
            data-testid="payout-verification-state"
          />
          <Input
            label={t('verifyPayout.form.programLabel')}
            value={programName}
            onChange={(e) => setProgramName(e.target.value)}
            data-testid="payout-verification-program"
          />
          <Input
            label={t('verifyPayout.form.contactLabel')}
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            data-testid="payout-verification-contact"
          />
          {error ? (
            <p className="text-sm text-red-600" data-testid="payout-verification-error">
              {error}
            </p>
          ) : null}
        </div>
      </Dialog.Panel>
      <Dialog.Actions>
        <Button
          label={t('verifyPayout.form.cancel')}
          size={WidgetSizeEnum.MEDIUM}
          onClick={onClose}
          disabled={submitting}
        />
        <Button
          label={submitting ? t('verifyPayout.form.submitting') : t('verifyPayout.form.submit')}
          primary
          size={WidgetSizeEnum.MEDIUM}
          loading={submitting}
          disabled={!canSubmit}
          onClick={handleSubmit}
          data-testid="payout-verification-submit"
        />
      </Dialog.Actions>
    </Dialog>
  );
}
